import * as React from "react";
import { useRouteError } from "react-router-dom";
import { ErrorLogger } from "./errorLogger";
import { Error403Message, Error500Message, ErrorOpenNetworkMessage } from "spry-react/Errors/errorMessages";
import { PortalPageNotFound } from "./portalPageNotFound";
import { PortalBaseErrorPage } from "./portalBaseErrorPage";
import {
  NotFoundRoutingErrorMessage,
  NetworkErrorMessage,
  Routing401ErrorMessage,
  Routing403ErrorMessage,
  Routing500ErrorMessage
} from "./routerErrorStrings";
import { usePortalAppContext  } from "../../Shared/appContext";

interface Props {
  errorLogger: ErrorLogger;
}

export function RouterErrorBoundary(props: Props) {
  const appName = "MatrixSAFE Portal"
  const error = useRouteError() as Error | string;
  const context = usePortalAppContext()
  const errorMessage = isError(error) ? error.message : error;
  
  // loaders can throw an error with this message to cause the 404 page to be displayed.
  if (errorMessage == NotFoundRoutingErrorMessage) {
    return <PortalPageNotFound />;
  } else if (errorMessage == NetworkErrorMessage) {
    return (
      <PortalBaseErrorPage
        title={`${appName} - Network Error`}
        headerContent={<h1>Network Error</h1>}
        errorMessage={<ErrorOpenNetworkMessage />}
        context={context}
      />
    );
  } else if (errorMessage == Routing401ErrorMessage) {
    return (
      <PortalBaseErrorPage
        title={`${appName} - Session Expired`}
        headerContent={<h1>Session Expired</h1>}
        errorMessage={
          <div>
            <h2>Your session has expired.</h2>
            Please refresh the page and login.
          </div>
        }
        context={context}
      />
    );
  } else if (errorMessage == Routing403ErrorMessage) {
    return (
      <PortalBaseErrorPage
        title={`${appName} - Permission Denied`}
        headerContent={<h1>Permission Denied</h1>}
        errorMessage={<Error403Message />}
        context={context}
      />
    );
  } else if (errorMessage == Routing500ErrorMessage) {
    return (
      <PortalBaseErrorPage
        title={`${appName} - Unexpected Error`}
        headerContent={<h1>Unexpected Error</h1>}
        errorMessage={<Error500Message />}
        context={context}
      />
    );
  } else {
    React.useEffect(() => {
      console.error(error);
    }, []);

    return (
      <PortalBaseErrorPage
        title={`${appName} - Unexpected Error`}
        headerContent={<h1>Unexpected Error</h1>}
        errorMessage={<Error500Message />}
        context={context}
      />
    );
  }

  function isError(error: Error | string): error is Error {
    return (error as Error).message !== undefined;
  }
}
